/*
 * Custom hook responsible for filtering and transforming dealership data.
 * Follows Single Responsibility Principle - only handles filtering and transformation logic.
 */

// externals
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

// internals
import { dealershipSelectors } from '../store';
import type { Dealership } from '@makemydeal/dr-sales-checkin-types';
import { useDealershipSelector } from '../selectors';
import { DealershipPickerMode } from '@makemydeal/dr-shared-types';

/**
 * Hook to filter dealerships based on mode and commonOrgId, and transform to select options
 * @param availableDealerships - Fallback dealership list
 * @param mode - Filter mode ('default' or 'all_dealerships')
 * @returns Object with filtered dealerships and auto-selected dealership
 */
export const useFilteredDealerships = (availableDealerships: Array<Dealership>, mode: DealershipPickerMode) => {
    const { getCommonOrgId } = dealershipSelectors.additionalSelectors;
    const commonOrgId = useSelector(getCommonOrgId ?? (() => ''));
    const { dealerships } = useDealershipSelector();

    return useMemo(() => {
        // Apply filtering based on mode
        const dealershipsArr = (dealerships.length > 0 ? dealerships : availableDealerships)
            .filter((dealership) => {
                // Skip filtering if mode is 'all_dealerships'
                if (mode === DealershipPickerMode.ALL_DEALERSHIPS) {
                    return true;
                }
                // Apply commonOrgId filtering for 'default' mode
                return commonOrgId ? dealership.commonOrgId === commonOrgId : true;
            })
            .map((dealership) => ({
                label: dealership.dealerName, // Label for the dealership selector dropdown
                value: `${dealership.dealerMMDId}` // Value for the dealership selector dropdown
            }));

        // Automatically select the dealership if there is only one filtered dealership
        const selectedDealership = dealershipsArr.length === 1 ? dealershipsArr[0].value : undefined;

        return { dealershipsArr, selectedDealership };
    }, [dealerships, availableDealerships, commonOrgId, mode]);
};
